/**
 * Duplicate Import Warning
 *
 * Advisory banner shown when analysis flags one or more files in this import
 * as matching a file from an earlier import in this org.
 */

import { AlertTriangle } from '@untitledui/icons';
import { useMemo } from 'react';

import type { AnalysisResult, FileSummary } from '@/lib/csv-import/api';

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

interface DuplicateImportEntry {
  filename: string;
  priorName: string;
  priorDate: string;
  matchType: 'content' | 'filename';
  requiresMatchColumns: boolean;
}

interface DuplicateImportWarningProps {
  analysisResult: AnalysisResult | null | undefined;
}

function hasDuplicateImport(summary: FileSummary): summary is FileSummary & {
  duplicate_import: NonNullable<FileSummary['duplicate_import']>;
} {
  return !!summary.duplicate_import;
}

function formatPriorDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
}

export function DuplicateImportWarning({
  analysisResult,
}: DuplicateImportWarningProps) {
  const entries = useMemo<DuplicateImportEntry[]>(() => {
    const fileSummaries = analysisResult?.file_summaries ?? {};
    return Object.values(fileSummaries)
      .filter(hasDuplicateImport)
      .map(summary => ({
        filename: summary.filename,
        priorName: summary.duplicate_import.prior_import_name,
        priorDate: formatPriorDate(summary.duplicate_import.prior_import_date),
        matchType: summary.duplicate_import.match_type,
        requiresMatchColumns: !!summary.requires_match_columns,
      }));
  }, [analysisResult]);

  if (entries.length === 0) {
    return null;
  }

  const anyRequiresMatchColumns = entries.some(
    entry => entry.requiresMatchColumns
  );

  return (
    <div
      role='status'
      className='p-3 bg-utility-warning-50 border border-utility-warning-200 rounded-lg'
    >
      <div className='flex items-start gap-2'>
        <AlertTriangle
          aria-hidden={true}
          className='w-4 h-4 text-utility-warning-600 flex-shrink-0 mt-0.5'
        />
        <div className='flex-1 space-y-1'>
          {entries.map(entry => (
            <p
              key={entry.filename}
              className='text-sm text-utility-warning-700'
            >
              {entry.matchType === 'content'
                ? `${entry.filename} is the same file you imported on ${entry.priorDate} as "${entry.priorName}".`
                : `A file named ${entry.filename} was imported on ${entry.priorDate} as "${entry.priorName}". This one's contents may differ.`}
            </p>
          ))}
          {anyRequiresMatchColumns && (
            <p className='text-sm text-utility-warning-700'>
              Mark a unique match column in the mapping step so existing records
              are updated instead of duplicated.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
